/**
 * 🩹 PATCH-350 (2026-02) · BAA Request page
 * ------------------------------------------
 * Public in-app flow for a covered entity to request a Business
 * Associate Agreement with LIFE LLC. Posts to
 * POST /api/legal/baa-request which persists the record and routes
 * an email to compliance@theexchangealliance.org.
 *
 * Also exposes a downloadable BAA template link (static asset).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Send, Loader2, CheckCircle2 } from "lucide-react";
import LegalPageShell from "../components/LegalPageShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

export default function BaaRequestPage() {
  const [form, setForm] = useState({
    organization_name: "",
    covered_entity_type: "",
    contact_name: "",
    contact_title: "",
    contact_email: "",
    contact_phone: "",
    phi_scope: "",
    additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/legal/baa-request`, form);
      setSubmittedId(data.request_id);
      toast.success("Request received. Compliance will respond within 3 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <LegalPageShell
        slug="baa-received"
        eyebrow="Legal · BAA"
        title="Request received"
        subtitle={`Reference ID · ${submittedId}`}
        lastUpdated="2026-02-25"
      >
        <div className="callout" data-testid="baa-submitted-confirmation">
          <strong>What happens next</strong>
          Our Compliance Officer will review your request and respond within
          <strong> 3 business days</strong> from{" "}
          <a href="mailto:compliance@theexchangealliance.org">compliance@theexchangealliance.org</a>.
          Please save the reference ID above.
        </div>
        <p>
          <CheckCircle2 className="inline h-5 w-5 text-[#0F766E] -mt-1 mr-1" />
          A confirmation email is on its way to <strong>{form.contact_email}</strong>.
        </p>
        <p>
          Meanwhile, you can review our{" "}
          <a href="/legal/security">Security &amp; Compliance</a> page or the{" "}
          <a href="/legal/hipaa">HIPAA Notice of Privacy Practices</a>.
        </p>
      </LegalPageShell>
    );
  }

  return (
    <LegalPageShell
      slug="baa"
      eyebrow="Legal · BAA"
      title="Business Associate Agreement"
      subtitle="Request or download a BAA before exchanging Protected Health Information with LIFE LLC."
      lastUpdated="2026-02-25"
    >
      <p>
        Under the HIPAA Privacy Rule, covered entities must execute a Business
        Associate Agreement with any vendor that will create, receive, maintain,
        or transmit Protected Health Information (PHI) on their behalf. LIFE LLC
        provides a standard BAA that follows the U.S. Department of Health &amp;
        Human Services model language plus the additional obligations under the
        HITECH Act (45 CFR § 164.504(e), § 164.314).
      </p>

      <h2>Two ways to proceed</h2>
      <ol>
        <li>
          <strong>Download the template</strong> — review our standard BAA
          language with your counsel, then countersign and return by email.
        </li>
        <li>
          <strong>Submit the request form below</strong> — we will send you our
          current BAA (customized for your covered-entity type) and route your
          request into our compliance queue for follow-up within 3 business days.
        </li>
      </ol>

      <div className="flex flex-wrap gap-3 my-6">
        <a
          href="/legal/baa-template-2026-02.pdf"
          download
          data-testid="baa-template-download"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-none bg-white border border-[#D4AF37] text-[#0A0612] font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[#FAF8F0]"
        >
          <Download className="h-4 w-4" /> Download BAA Template
        </a>
        <a
          href="#request-form"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-none bg-[#0A0612] text-white font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[#1a1030]"
        >
          <Send className="h-4 w-4" /> Jump to Request Form
        </a>
      </div>

      <div className="callout">
        <strong>Note</strong>
        Downloading the template does not create an executed BAA. A BAA is only
        binding once countersigned by both LIFE LLC and your organization's
        authorized signatory.
      </div>

      <h2 id="request-form">Request Form</h2>
      <p>All fields marked with an asterisk are required.</p>
      <form
        onSubmit={submit}
        data-testid="baa-request-form"
        className="mt-6 p-6 bg-white border border-[#E8E5DA] font-sans text-sm space-y-5"
      >
        <FormField label="Organization Name *" required>
          <Input required value={form.organization_name} onChange={set("organization_name")} data-testid="baa-input-org" />
        </FormField>

        <FormField label="Covered Entity Type *" required>
          <select
            required
            value={form.covered_entity_type}
            onChange={set("covered_entity_type")}
            data-testid="baa-input-type"
            className="w-full h-10 px-3 border border-[#E8E5DA] focus:outline-none focus:border-[#D4AF37] bg-white text-sm"
          >
            <option value="">Select one…</option>
            <option value="health_plan">Health Plan</option>
            <option value="clearinghouse">Healthcare Clearinghouse</option>
            <option value="provider">Healthcare Provider</option>
            <option value="business_associate">Business Associate (subcontract)</option>
            <option value="government_agency">Government Agency</option>
            <option value="other">Other</option>
          </select>
        </FormField>

        <div className="grid sm:grid-cols-2 gap-4">
          <FormField label="Contact Name *" required>
            <Input required value={form.contact_name} onChange={set("contact_name")} data-testid="baa-input-contact-name" />
          </FormField>
          <FormField label="Contact Title *" required>
            <Input required value={form.contact_title} onChange={set("contact_title")} data-testid="baa-input-contact-title" />
          </FormField>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <FormField label="Contact Email *" required>
            <Input required type="email" value={form.contact_email} onChange={set("contact_email")} data-testid="baa-input-contact-email" />
          </FormField>
          <FormField label="Contact Phone">
            <Input type="tel" value={form.contact_phone} onChange={set("contact_phone")} data-testid="baa-input-contact-phone" />
          </FormField>
        </div>

        <FormField label="Scope of PHI *" required>
          <Textarea
            required
            rows={3}
            value={form.phi_scope}
            onChange={set("phi_scope")}
            data-testid="baa-input-phi-scope"
            placeholder="Briefly describe the categories and volume of PHI you expect to share (e.g., referral records for approximately 50 participants per year)."
          />
        </FormField>

        <FormField label="Additional Notes">
          <Textarea
            rows={3}
            value={form.additional_notes}
            onChange={set("additional_notes")}
            data-testid="baa-input-notes"
            placeholder="Timeline, jurisdiction-specific riders, or anything else our counsel should know."
          />
        </FormField>

        <div className="text-xs text-[#6B6F76] leading-relaxed">
          By submitting, you consent to LIFE LLC contacting you at the address
          above regarding your BAA request. This form is not a substitute for a
          fully executed BAA — no PHI should be transmitted through this form.
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="baa-submit"
          className="w-full h-12 rounded-none bg-[#0A0612] hover:bg-[#1a1030] text-white font-mono text-[11px] tracking-[0.22em] uppercase"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
          ) : (
            <><Send className="h-4 w-4 mr-2" /> Submit BAA Request</>
          )}
        </Button>
      </form>
    </LegalPageShell>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-2">{label}</div>
      {children}
    </label>
  );
}
